import { Link } from "react-router-dom";
import type { Asset } from "@/data/types";
import { drillHref, type NavState } from "@/navigation/trail";
import { Card, Fld, PaneHeader, Totals } from "./parts";

export default function QualityTab({
  asset,
  state,
}: {
  asset: Asset;
  state: NavState;
}) {
  const current = asset.versions.at(-1)!;
  const conforming = asset.qualityChecks.filter((c) => c.pass).length;
  const failing = asset.qualityChecks.length - conforming;
  const maxDeviation = Math.max(...asset.versions.map((v) => v.cadDeviation));

  return (
    <section className="pane">
      <PaneHeader
        asset={asset}
        h2="Qualità / CQ"
        sub={
          <>
            {asset.ref} · {current.id} · CONTROLLO {asset.qcDate} · OPERATORE{" "}
            {asset.qcOperator.toUpperCase()} · NORMA INTERNA CQ-3D/04
          </>
        }
      />

      <Card
        title="◱ Esito del controllo"
        note={`${asset.qualityChecks.length} controlli · ${conforming} conformi`}
      >
        <div className="fgrid">
          <Fld
            label="Esito complessivo"
            value={failing ? `${failing} non conforme` : "Tutti conformi"}
            tone={failing ? "warn" : "pos"}
          />
          <Fld
            label="Controlli conformi"
            value={`${conforming} / ${asset.qualityChecks.length}`}
            tone="pos"
          />
          <Fld label="Data controllo" value={asset.qcDate} />
          <Fld label="Operatore" value={asset.qcOperator} />
          <Fld label="Norma applicata" value={asset.qcStandard} />
          <Fld label="Stato rilascio" value={asset.qcRelease} />
        </div>

        {asset.qcAlert && (
          <div className="alert">
            <span className="ic">▲</span>
            <span className="tx">
              <b>{asset.qcAlert}</b> Scostamento localizzato sul fondello, in
              corrispondenza del pavé. Azione proposta: ripetere il rilievo con
              passo 0.02 mm sulla sola zona interessata, oppure rilascio in
              deroga con firma del responsabile CQ.
            </span>
          </div>
        )}
      </Card>

      <Card
        title="▦ Controlli mesh e dimensionali"
        note={`${current.id} · ${current.date}`}
      >
        <div className="tw">
          <table className="pt">
            <thead>
              <tr>
                <th>Controllo</th>
                <th>Metodo</th>
                <th className="num">Soglia</th>
                <th className="num">Misurato</th>
                <th className="num">Delta</th>
                <th>Esito</th>
              </tr>
            </thead>
            <tbody>
              {asset.qualityChecks.map((c) => (
                <tr key={c.label} className={c.pass ? undefined : "cur"}>
                  <td className="nm">{c.label}</td>
                  <td>{c.method}</td>
                  <td className="num">{c.threshold}</td>
                  <td className={`num${c.pass ? "" : " warn"}`}>
                    {c.measured}
                  </td>
                  <td className={`num ${c.pass ? "pos" : "warn"}`}>
                    {c.delta}
                  </td>
                  <td>
                    <span className={`st ${c.pass ? "ok" : "ko"}`}>
                      {c.pass ? "conforme" : "fuori soglia"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Totals
          items={[
            ["Controlli", String(asset.qualityChecks.length)],
            ["Conformi", String(conforming)],
            ["Fuori soglia", String(failing)],
            ["Deviazione max", `${maxDeviation.toFixed(2)} mm`],
          ]}
        />
      </Card>

      <Card
        title="▦ Storico per versione"
        note="la deviazione CAD si riduce a ogni revisione"
      >
        <div className="tw">
          <table className="pt">
            <thead>
              <tr>
                <th>Versione</th>
                <th>Data</th>
                <th className="num">Controlli</th>
                <th className="num">Conformi</th>
                <th className="num">Deviazione CAD</th>
                <th>Esito</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {asset.versions.map((v) => {
                const ok = v.cadDeviation <= 0.1;
                let conformita: string;
                if (ok) conformita = "conforme";
                else if (v.status === "corrente") conformita = "in deroga";
                else conformita = "non conforme";
                return (
                  <tr
                    key={v.id}
                    className={v.status === "corrente" ? "cur" : undefined}
                  >
                    <td className="mono">{v.id}</td>
                    <td className="mono">{v.date}</td>
                    <td className="num">{asset.qualityChecks.length}</td>
                    <td className="num">{v.conforming}</td>
                    <td className={`num${ok ? "" : " warn"}`}>
                      {v.cadDeviation.toFixed(2)} mm
                    </td>
                    <td>
                      <span className={`st ${ok ? "ok" : "ko"}`}>
                        {conformita}
                      </span>
                    </td>
                    <td>
                      <Link
                        className="simili"
                        to={drillHref(asset.ref, state, "version", v.id)}
                        title={`Trova scansioni simili alla ${v.id}`}
                      >
                        ✦ simili
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </section>
  );
}
